import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Flame,
  TrendingUp,
  DollarSign,
  ShoppingCart,
  Users,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  MapPin,
  ChefHat,
  Receipt,
  Settings2,
  Globe,
  Package,
  Timer,
  Check,
  Info,
  Bell,
  Plus,
  X,
  Coffee,
  LogOut,
} from 'lucide-react';
import {
  Line,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { OnboardingTourTrigger } from '@/components/onboarding/OnboardingTourTrigger';
import { useAuth } from '@/hooks/useAuth';
import { useLocations } from '@/hooks/useLocations';
import { useCurrency } from '@/hooks/useCurrency';
import { useRecentActivity } from '@/hooks/useRecentActivity';
import { useAnalytics, AVAILABLE_STATS, StatKey, AnalyticsSummary, TimePeriod } from '@/hooks/useAnalytics';
import { useDashboardPreferences } from '@/hooks/useDashboardPreferences';
import { usePermissions } from '@/hooks/usePermissions';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useRegisterSession } from '@/hooks/useRegisterSession';
import { useAssistanceRequests } from '@/hooks/useAssistanceRequests';
import {
  OpenRegisterModal,
  RequestAssistanceModal,
  AssistanceNotificationBanner,
} from '@/components/register';
import { ReceiptReprintDialog } from '@/components/orders/ReceiptReprintDialog';
import { NotificationBell } from '@/components/notifications';
import { HelpButton } from '@/components/HelpButton';
import { useSalesTargetProgress, getProgressBarColor } from '@/hooks/useSalesTargets';
import { useRetailMetrics } from '@/hooks/useRetailMetrics';
import { useIndustryTerms } from '@/hooks/useIndustryTerms';
import { RetailMetricsModal } from '@/components/dashboard/RetailMetricsModal';
import { RetailMetricCards } from '@/components/dashboard/RetailMetricCards';

// Icon mapping for stats
const STAT_ICONS: Record<StatKey, typeof DollarSign> = {
  todays_sales: DollarSign,
  order_count: ShoppingCart,
  avg_ticket: TrendingUp,
  guest_count: Users,
  avg_prep_time: Timer,
  items_sold: Package,
};

// Helper to get stat value and change from summary
function getStatData(
  key: StatKey, 
  summary: AnalyticsSummary | null, 
  formatCurrency: (value: number) => string
): { value: string; change: number; trend: 'up' | 'down' | 'neutral' } {
  if (!summary) {
    return { value: '—', change: 0, trend: 'neutral' };
  }

  switch (key) {
    case 'todays_sales':
      return {
        value: formatCurrency(summary.totalSales),
        change: summary.salesPercentChange,
        trend: summary.salesPercentChange >= 0 ? 'up' : 'down',
      };
    case 'order_count':
      return {
        value: summary.orderCount.toString(),
        change: summary.orderPercentChange,
        trend: summary.orderPercentChange >= 0 ? 'up' : 'down',
      };
    case 'avg_ticket':
      return {
        value: formatCurrency(summary.averageOrderValue),
        change: summary.aovPercentChange,
        trend: summary.aovPercentChange >= 0 ? 'up' : 'down',
      };
    case 'guest_count':
      return {
        value: summary.guestCount.toString(),
        change: summary.guestCountPercentChange,
        trend: summary.guestCountPercentChange >= 0 ? 'up' : 'down',
      };
    case 'avg_prep_time':
      // For prep time, lower is better, so invert the trend
      const prepTimeFormatted = summary.avgPrepTime > 0 
        ? `${summary.avgPrepTime.toFixed(1)} min` 
        : '—';
      return {
        value: prepTimeFormatted,
        change: summary.avgPrepTimePercentChange,
        trend: summary.avgPrepTimePercentChange <= 0 ? 'up' : 'down', // Inverted: lower is better
      };
    case 'items_sold':
      return {
        value: summary.itemsSold.toString(),
        change: summary.itemsSoldPercentChange,
        trend: summary.itemsSoldPercentChange >= 0 ? 'up' : 'down',
      };
    default:
      return { value: '—', change: 0, trend: 'neutral' };
  }
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, profile, organization, loading, isAuthenticated } = useAuth();
  const { locations, currentLocation, setCurrentLocation } = useLocations();
  const { formatCurrency } = useCurrency();
  const { effectiveRole, hasPermission } = usePermissions();
  const industryTerms = useIndustryTerms();

  // Check if user can view sales reports/metrics
  const canViewSalesMetrics = hasPermission('reports.view_sales');

  // Cashiers and employees need register management and see limited dashboard
  const isCashier = effectiveRole === 'cashier' || effectiveRole === 'employee';

  // Register session management (for cashiers)
  const { activeSession, isRegisterOpen, isLoading: registerLoading, pauseSession } = useRegisterSession();
  const [showOpenRegisterModal, setShowOpenRegisterModal] = useState(false);
  const [showAssistanceModal, setShowAssistanceModal] = useState(false);
  const [showReceiptReprintDialog, setShowReceiptReprintDialog] = useState(false);

  // Assistance requests (for managers)
  const { pendingRequests, myActiveRequest, isManager } = useAssistanceRequests();
  
  // Feature flags for UI toggles
  const { flags } = useFeatureFlags();

  // Auto-start onboarding tour when coming from the questionnaire
  const shouldStartTour = searchParams.get('onboarded') === 'true';
  useEffect(() => {
    if (shouldStartTour) {
      setSearchParams({}, { replace: true });
    }
  }, [shouldStartTour, setSearchParams]);
  
  // Time period selector state (Today, This Week, This Month)
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('today');
  
  // Check if user should default to all locations view (admins with multiple locations)
  const isAdminRole = effectiveRole === 'superadmin' || effectiveRole === 'primary_admin' || 
                      effectiveRole === 'owner' || effectiveRole === 'admin';
  const hasMultipleLocations = locations.length > 1;
  
  // Dashboard preferences (configurable stats, global view, retail widgets)
  const { 
    selectedStats, 
    showGlobalView, 
    enabledWidgets,
    setShowGlobalView,
    setSelectedStats,
    setEnabledWidgets,
  } = useDashboardPreferences({
    userId: user?.id,
    defaultShowGlobalView: isAdminRole && hasMultipleLocations,
  });

  // Modal state for retail metrics picker
  const [showRetailMetricsModal, setShowRetailMetricsModal] = useState(false);

  // Recent activity - respects global view setting
  const { recentOrders, isLoading: activityLoading, getTimeAgo, getStatusInfo } = useRecentActivity(
    8,
    {
      filterByUserId: isCashier ? user?.id : undefined,
      showAllLocations: showGlobalView && !isCashier,
    }
  );

  // Analytics data - pass global mode flag and time period
  const {
    hourlyData,
    summary,
    isLoading: analyticsLoading,
    locationCount,
  } = useAnalytics({ globalMode: showGlobalView, timePeriod });

  // Retail metrics widget data (fetches only for enabled widgets)
  const retailMetrics = useRetailMetrics({
    timePeriod,
    globalMode: showGlobalView,
    enabledWidgets,
  });

  // Sales target progress for the dashboard card
  const { data: salesTargetProgress } = useSalesTargetProgress(
    currentLocation?.id,
    'daily'
  );

  // Dashboard Todo List - persisted to localStorage
  interface TodoItem {
    id: string;
    text: string;
    completed: boolean;
    createdAt: number;
  }

  const TODOS_STORAGE_KEY = 'blazepos_dashboard_todos';

  const [todos, setTodos] = useState<TodoItem[]>(() => {
    try {
      const stored = localStorage.getItem(TODOS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [newTodoText, setNewTodoText] = useState('');

  // Persist todos to localStorage
  useEffect(() => {
    localStorage.setItem(TODOS_STORAGE_KEY, JSON.stringify(todos));
  }, [todos]);

  const addTodo = useCallback(() => {
    const text = newTodoText.trim();
    if (!text) return;
    setTodos(prev => [...prev, {
      id: crypto.randomUUID(),
      text,
      completed: false,
      createdAt: Date.now(),
    }]);
    setNewTodoText('');
  }, [newTodoText]);

  const toggleTodo = useCallback((id: string) => {
    setTodos(prev => prev.map(t =>
      t.id === id ? { ...t, completed: !t.completed } : t
    ));
  }, []);

  const removeTodo = useCallback((id: string) => {
    setTodos(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  // Show open register modal for cashiers without an open register
  useEffect(() => {
    if (!loading && !registerLoading && isCashier && !isRegisterOpen) {
      setShowOpenRegisterModal(true);
    }
  }, [loading, registerLoading, isCashier, isRegisterOpen]);

  // Build stats array from selected stats and real data
  const stats = useMemo(() => {
    return selectedStats.map((statKey) => {
      const statInfo = AVAILABLE_STATS.find(s => s.key === statKey);
      const data = getStatData(statKey, summary, formatCurrency);
      return {
        key: statKey,
        title: statInfo?.label ?? statKey,
        value: data.value,
        change: `${data.change >= 0 ? '+' : ''}${data.change.toFixed(1)}%`,
        trend: data.trend,
        icon: STAT_ICONS[statKey],
      };
    });
  }, [selectedStats, summary, formatCurrency]);

  // Get period-specific labels
  const periodLabels = useMemo(() => {
    switch (timePeriod) {
      case 'today':
        return {
          title: "Today's sales",
          comparisonPrefix: 'This time last',
          chartLabel: 'Today',
        };
      case 'week':
        return {
          title: "This week's sales",
          comparisonPrefix: 'Same period last',
          chartLabel: 'This week',
        };
      case 'month':
        return {
          title: "This month's sales",
          comparisonPrefix: 'Same period last',
          chartLabel: 'This month',
        };
    }
  }, [timePeriod]);

  // Transform hourly/daily data for chart
  const salesTrend = useMemo(() => {
    if (!hourlyData || hourlyData.length === 0) {
      return [];
    }

    if (timePeriod === 'today') {
      // Show all 24 hours so chart matches the total sales figure
      return hourlyData.map(data => ({
        label: data.hour.replace(' AM', 'am').replace(' PM', 'pm'),
        sales: data.sales,
        lastWeekSales: data.lastWeekSales,
      }));
    } else {
      // For week/month, use the daily data directly
      return hourlyData.map(data => ({
        label: data.hour, // Already formatted as "Mon 25"
        sales: data.sales,
        lastWeekSales: data.lastWeekSales,
      }));
    }
  }, [hourlyData, timePeriod]);

  // Mini chart data: 6-hour window for today, or recent days for week/month
  const miniChartData = useMemo(() => {
    if (!hourlyData || hourlyData.length === 0) {
      return [];
    }

    if (timePeriod === 'today') {
      const now = new Date();
      const currentHour = now.getHours();
      // Show 3 hours before and 3 hours after current hour (or adjust if near start/end of day)
      const startHour = Math.max(0, currentHour - 3);
      const endHour = Math.min(23, startHour + 5); // 6-hour window

      return hourlyData
        .filter((_, index) => index >= startHour && index <= endHour)
        .map(data => ({
          label: data.hour.replace(' AM', 'am').replace(' PM', 'pm'),
          itemsSold: data.itemsSold,
          lastWeekItemsSold: data.lastWeekItemsSold,
        }));
    } else {
      // For week/month, show the last 6 days of data
      const recentDays = hourlyData.slice(-6);
      return recentDays.map(data => ({
        label: data.hour, // Already formatted as "Mon 25"
        itemsSold: data.itemsSold,
        lastWeekItemsSold: data.lastWeekItemsSold,
      }));
    }
  }, [hourlyData, timePeriod]);

  // Handle stat toggle in dropdown
  const handleStatToggle = (statKey: StatKey) => {
    const isSelected = selectedStats.includes(statKey);
    
    if (isSelected) {
      // Can't deselect if we only have 4
      if (selectedStats.length <= 4) {
        return;
      }
      setSelectedStats(selectedStats.filter(s => s !== statKey));
    } else {
      // Replace the last one if we already have 4
      if (selectedStats.length >= 4) {
        setSelectedStats([...selectedStats.slice(0, 3), statKey]);
      } else {
        setSelectedStats([...selectedStats, statKey]);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center animate-pulse overflow-hidden">
            <img src="/blazepos-logo.png" alt="BlazePOS" className="w-10 h-10 object-contain" />
          </div>
          <span className="text-xl font-display font-bold">Loading...</span>
        </div>
      </div>
    );
  }

  const header = (
    <div className="flex w-full items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-muted-foreground">Dashboard</span>
        <span className="text-muted-foreground/40">/</span>
        <span className="text-sm font-medium">Overview</span>
      </div>

      <div className="flex items-center gap-4">
        {/* Location Selector with Global option - hidden for cashiers/employees */}
        {!isCashier && locations.length > 0 && (
          <div className="flex items-center gap-2">
            {showGlobalView ? (
              <Globe className="h-4 w-4 text-primary" />
            ) : (
              <MapPin className="h-4 w-4 text-muted-foreground" />
            )}
            <Select
              value={showGlobalView ? 'all' : (currentLocation?.id || '')}
              onValueChange={(value) => {
                if (value === 'all') {
                  setShowGlobalView(true);
                } else {
                  setShowGlobalView(false);
                  const loc = locations.find((l) => l.id === value);
                  if (loc) setCurrentLocation(loc);
                }
              }}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                {locations.length > 1 && (
                  <>
                    <SelectItem value="all">
                      <div className="flex items-center gap-2">
                        <Globe className="h-3.5 w-3.5" />
                        <span>All Locations</span>
                      </div>
                    </SelectItem>
                    <div className="my-1 h-px bg-border" />
                  </>
                )}
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Help & Notifications */}
        <HelpButton />
        <NotificationBell />

        <div className="flex items-center gap-3 px-3 py-2">
          <div className="text-right">
            <p className="text-sm font-medium">
              {organization?.name ?? 'BlazePOS Demo'}
            </p>
          </div>
          {organization?.logo_url ? (
            <div className="h-9 w-9 rounded-lg overflow-hidden bg-white flex items-center justify-center border">
              <img 
                src={organization.logo_url} 
                alt={organization.name || 'Organization logo'} 
                className="h-full w-full object-contain"
              />
            </div>
          ) : (
            <div className="h-9 w-9 rounded-lg flex items-center justify-center overflow-hidden">
              <img src="/blazepos-logo.png" alt="BlazePOS" className="h-9 w-9 object-contain" />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout header={header}>
      <OnboardingTourTrigger />
      {/* Lightspeed-style Welcome Header with Period Tabs */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-semibold tracking-tight">
              {isCashier 
                ? `Welcome back${profile?.first_name ? `, ${profile.first_name}` : ''}`
                : `Hi ${organization?.name || 'there'}, here's what's happening in your stores`
              }
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {isCashier
                ? activeSession?.status === 'open'
                  ? `You're on ${activeSession?.register_name || 'register'}. Ready to take orders!`
                  : activeSession?.status === 'paused'
                  ? `You're on break from ${activeSession?.register_name || 'register'}. Resume when ready!`
                  : 'Open a register to start taking orders.'
                : 'Stay on top of your sales, orders, and customers in real time.'}
            </p>
          </div>
          
          {/* Period Tabs - only show for non-cashiers with metrics access */}
          {!isCashier && canViewSalesMetrics && (
            <div className="flex items-center gap-2">
              <div className="flex items-center rounded-lg border bg-card p-1">
                <button
                  onClick={() => setTimePeriod('today')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    timePeriod === 'today' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Today
                </button>
                <button
                  onClick={() => setTimePeriod('week')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    timePeriod === 'week' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  This week
                </button>
                <button
                  onClick={() => setTimePeriod('month')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    timePeriod === 'month' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  This month
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Assistance Notification Banner for Managers */}
      {isManager && pendingRequests.length > 0 && (
        <AssistanceNotificationBanner />
      )}

      {/* Global View Notice - controlled by feature flag */}
      {flags.showGlobalViewNotice && showGlobalView && locationCount > 1 && canViewSalesMetrics && (
        <div className="mb-6 flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
          <Info className="h-4 w-4 text-primary shrink-0" />
          <p className="text-sm text-primary">
            Viewing aggregated data across <span className="font-semibold">{locationCount} locations</span>
          </p>
        </div>
      )}

      {/* Stats Grid with Settings - Hidden for users without sales report access or when feature flag is off */}
      {canViewSalesMetrics && flags.showClassicDashboardCards && (
      <div className="mb-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Today's Metrics
          </h2>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 gap-1.5">
                <Settings2 className="h-4 w-4" />
                <span className="hidden sm:inline">Customize</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Choose 4 metrics to display</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {AVAILABLE_STATS.map((stat) => {
                const isSelected = selectedStats.includes(stat.key);
                const Icon = STAT_ICONS[stat.key];
                return (
                  <DropdownMenuItem
                    key={stat.key}
                    onClick={() => handleStatToggle(stat.key)}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <div className={`flex h-5 w-5 items-center justify-center rounded ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                      {isSelected ? <Check className="h-3 w-3" /> : <Icon className="h-3 w-3 text-muted-foreground" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm">{stat.label}</p>
                    </div>
                    {isSelected && selectedStats.length <= 4 && (
                      <span className="text-xs text-muted-foreground">Required</span>
                    )}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        <div id="tour-dashboard-stats" className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
          {analyticsLoading ? (
            // Loading skeleton
            Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="relative overflow-hidden animate-pulse">
                <CardHeader className="relative flex flex-row items-center justify-between pb-2">
                  <div className="h-4 w-24 bg-muted rounded" />
                  <div className="h-9 w-9 rounded-xl bg-muted" />
                </CardHeader>
                <CardContent className="relative">
                  <div className="h-8 w-32 bg-muted rounded mb-2" />
                  <div className="h-4 w-24 bg-muted rounded" />
                </CardContent>
              </Card>
            ))
          ) : (
            stats.map((stat) => (
              <Card
                key={stat.key}
                className="relative overflow-hidden"
              >
                <div className="pointer-events-none absolute inset-0 opacity-[0.04]">
                  <div className="pattern-dots" />
                </div>
                <CardHeader className="relative flex flex-row items-center justify-between pb-2">
                  <div>
                    <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {stat.title}
                    </CardTitle>
                  </div>
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-muted">
                    <stat.icon className="h-4 w-4 text-muted-foreground" />
                  </div>
                </CardHeader>
                <CardContent className="relative">
                  <div className="flex items-end justify-between gap-2">
                    <div>
                      <div className="text-2xl font-semibold tracking-tight">
                        {stat.value}
                      </div>
                      <div className="mt-1 flex items-center text-xs">
                        {stat.trend === 'up' ? (
                          <ArrowUpRight className="mr-1 h-3 w-3 text-emerald-500" />
                        ) : stat.trend === 'down' ? (
                          <ArrowDownRight className="mr-1 h-3 w-3 text-red-500" />
                        ) : null}
                        <span
                          className={
                            stat.trend === 'up'
                              ? 'text-emerald-500'
                              : stat.trend === 'down'
                              ? 'text-red-500'
                              : 'text-muted-foreground'
                          }
                        >
                          {stat.change}
                        </span>
                        <span className="ml-1 text-muted-foreground">
                          vs last {summary?.comparisonDayName || 'week'}
                        </span>
                      </div>
                    </div>
                    {stat.trend !== 'neutral' && (
                      <Badge
                        variant={stat.trend === 'up' ? 'success' : 'destructive'}
                        className="shrink-0"
                      >
                        {stat.trend === 'up' ? 'Healthy' : 'Needs attention'}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
      )}

      {/* Sales Overview - Full Width */}
      {canViewSalesMetrics && (
        <Card className="mb-6">
            <CardHeader className="pb-4">
              {/* Stats Row - Total Sales | Avg Sale | Avg Items */}
              <div className="flex items-start justify-between">
                <div className="flex items-start divide-x divide-border">
                  {/* Total Sales - Primary */}
                  <div className="pr-6">
                    <CardTitle className="text-lg">{periodLabels.title}</CardTitle>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-3xl font-semibold tracking-tight">
                        {formatCurrency(summary?.totalSales || 0)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {periodLabels.comparisonPrefix} {summary?.comparisonDayName || 'week'}: {formatCurrency(summary?.lastWeekTotalSales || 0)}
                    </p>
                  </div>

                  {/* Average Sale Value - Secondary */}
                  <div className="px-6">
                    <p className="text-sm text-muted-foreground">Average sale value</p>
                    <p className="text-xl font-semibold tracking-tight mt-1">
                      {formatCurrency(summary?.averageOrderValue || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {formatCurrency(Math.abs(summary?.aovDifference || 0))} {(summary?.aovDifference || 0) >= 0 ? 'more' : 'less'} than last {summary?.comparisonDayName || 'week'}
                    </p>
                  </div>

                  {/* Average Items per Sale - Secondary */}
                  <div className="pl-6">
                    <p className="text-sm text-muted-foreground">Average items per sale</p>
                    <p className="text-xl font-semibold tracking-tight mt-1">
                      {summary?.averageItemsPerSale?.toFixed(1) || '0'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {Math.abs(summary?.itemsDifference || 0).toFixed(2)} {(summary?.itemsDifference || 0) >= 0 ? 'more' : 'less'} than last {summary?.comparisonDayName || 'week'}
                    </p>
                  </div>
                </div>

                <Badge variant="outline">{showGlobalView ? 'All locations' : currentLocation?.name || 'All locations'}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {/* Chart Section - Full Width */}
              <div className="h-64">
                {analyticsLoading ? (
                  <div className="flex h-full items-center justify-center">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <img src="/blazepos-logo.png" alt="" className="h-4 w-4 animate-pulse" />
                      <span className="text-sm">Loading chart...</span>
                    </div>
                  </div>
                ) : salesTrend.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center text-center">
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                      <TrendingUp className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <p className="text-sm font-medium">No sales data yet</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Sales will appear here as orders are placed
                    </p>
                  </div>
                ) : (
                  <ChartContainer
                    config={{
                      sales: {
                        label: periodLabels.chartLabel,
                        color: "#3b82f6",
                      },
                      lastWeekSales: {
                        label: `Last ${summary?.comparisonDayName || 'week'}`,
                        color: "hsl(var(--muted-foreground))",
                      },
                    } satisfies ChartConfig}
                    className="h-full w-full"
                  >
                    <LineChart data={salesTrend}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        className="stroke-muted/30"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="label"
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => {
                          if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
                          return `${value}`;
                        }}
                        tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                        width={40}
                      />
                      <ChartTooltip
                        cursor={{ stroke: 'hsl(var(--muted))', strokeWidth: 1 }}
                        content={
                          <ChartTooltipContent
                            indicator="dot"
                            valueFormatter={(value) => formatCurrency(value)}
                          />
                        }
                      />
                      {/* Last week line (background, gray) */}
                      <Line
                        type="monotone"
                        dataKey="lastWeekSales"
                        stroke="var(--color-lastWeekSales)"
                        strokeWidth={1.5}
                        strokeOpacity={0.4}
                        dot={false}
                        activeDot={{ r: 4, strokeWidth: 0, fill: 'hsl(var(--muted-foreground))' }}
                      />
                      {/* Today line (primary, blue with dots) */}
                      <Line
                        type="monotone"
                        dataKey="sales"
                        stroke="var(--color-sales)"
                        strokeWidth={2.5}
                        dot={{ r: 4, strokeWidth: 2, stroke: 'var(--color-sales)', fill: 'white' }}
                        activeDot={{ r: 6, strokeWidth: 2, stroke: 'var(--color-sales)', fill: 'white' }}
                      />
                    </LineChart>
                  </ChartContainer>
                )}
              </div>
            </CardContent>
          </Card>
      )}

      {/* Things to Know - Retail Metrics Widgets */}
      {canViewSalesMetrics && (
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Things to know
            </h2>
            <button
              onClick={() => setShowRetailMetricsModal(true)}
              className="flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              <Plus className="h-3.5 w-3.5" />
              Show more Retail metrics
            </button>
          </div>
          {enabledWidgets.length > 0 && (
            <RetailMetricCards
              enabledWidgets={enabledWidgets}
              metricsData={retailMetrics}
              formatCurrency={formatCurrency}
            />
          )}
        </div>
      )}

      {/* Sales Target + Mini Chart + Todo - Row with max height */}
      {canViewSalesMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 h-80">
          {/* Sales Targets Card */}
          <Card className="h-full">
            <CardContent className="pt-6 h-full flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Daily Sales Target</h3>
                <Link
                  to="/command-center/sales-targets"
                  className="text-sm text-primary hover:underline"
                >
                  {salesTargetProgress?.target_id ? 'Manage' : 'Set a target'}
                </Link>
              </div>

              {salesTargetProgress?.target_id ? (
                <div className="space-y-3 flex-1">
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${getProgressBarColor(salesTargetProgress.sales_progress_pct)}`}
                      style={{ width: `${Math.min(salesTargetProgress.sales_progress_pct, 100)}%` }}
                    />
                  </div>

                  <div className="flex items-baseline gap-2">
                    <p className="text-2xl font-semibold">
                      {formatCurrency(salesTargetProgress.actual_sales)}
                    </p>
                    <span className="text-sm text-muted-foreground">
                      / {formatCurrency(salesTargetProgress.target_sales)}
                    </span>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    {salesTargetProgress.sales_progress_pct.toFixed(0)}% of daily target
                    {salesTargetProgress.sales_progress_pct >= 100 && ' - Target reached!'}
                  </p>

                  <div className="flex gap-4 pt-2 border-t text-xs text-muted-foreground">
                    <span>{salesTargetProgress.actual_orders} / {salesTargetProgress.target_orders} orders</span>
                    <span>Avg: {formatCurrency(salesTargetProgress.actual_avg_ticket)}</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 flex-1">
                  <div className="h-2 bg-muted rounded-full" />
                  <p className="text-2xl font-semibold">$0.00</p>
                  <p className="text-sm text-muted-foreground">
                    No target set. Set a daily sales target to track your progress.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Your Sales Mini Chart - with axes */}
          <Card className="h-full">
            <CardContent className="pt-6 h-full flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">
                  Items sold {timePeriod === 'today' ? 'today' : timePeriod === 'week' ? 'this week' : 'this month'}
                </h3>
                <span className="text-xs text-muted-foreground">
                  {timePeriod === 'today' ? '6-hour window' : 'Recent days'}
                </span>
              </div>
              <div className="flex-1 min-h-[180px]">
                {miniChartData.length > 0 ? (
                  <ChartContainer
                    config={{
                      itemsSold: {
                        label: "Items sold",
                        color: "#3b82f6",
                      },
                    } satisfies ChartConfig}
                    className="h-full w-full"
                  >
                    <LineChart data={miniChartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" vertical={false} />
                      <XAxis
                        dataKey="label"
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                        width={30}
                        allowDecimals={false}
                      />
                      <ChartTooltip
                        cursor={{ stroke: 'hsl(var(--muted))', strokeWidth: 1 }}
                        content={
                          <ChartTooltipContent
                            indicator="dot"
                            valueFormatter={(value) => `${value} items`}
                          />
                        }
                      />
                      <Line
                        type="monotone"
                        dataKey="itemsSold"
                        name="Items sold"
                        stroke="var(--color-itemsSold)"
                        strokeWidth={2}
                        dot={{ r: 3, fill: 'var(--color-itemsSold)' }}
                        activeDot={{ r: 5, strokeWidth: 0, fill: 'var(--color-itemsSold)' }}
                      />
                    </LineChart>
                  </ChartContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                    No data yet
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Things to Do Section - Functional with persistence */}
          <Card className="h-full flex flex-col overflow-hidden">
            <CardHeader className="pb-2 shrink-0">
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Things to do
              </h2>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col min-h-0">
              {/* Add todo input */}
              <div className="flex gap-2 mb-3 shrink-0">
                <Input
                  placeholder="Add a task..."
                  value={newTodoText}
                  onChange={(e) => setNewTodoText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addTodo();
                  }}
                  className="h-8 text-sm"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addTodo}
                  disabled={!newTodoText.trim()}
                  className="h-8 px-2"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* Todo list with scroll */}
              <div className="flex-1 overflow-y-auto min-h-0">
                {todos.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-4 text-center">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center mb-2">
                      <svg
                        className="w-5 h-5 text-muted-foreground"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                        />
                      </svg>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      No tasks yet
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {todos
                      .sort((a, b) => {
                        // Uncompleted first, then by creation time
                        if (a.completed !== b.completed) return a.completed ? 1 : -1;
                        return b.createdAt - a.createdAt;
                      })
                      .map((todo) => (
                        <div
                          key={todo.id}
                          className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50 transition-colors"
                        >
                          <button
                            onClick={() => toggleTodo(todo.id)}
                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                              todo.completed
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-muted-foreground/30 hover:border-primary'
                            }`}
                          >
                            {todo.completed && <Check className="h-3 w-3" />}
                          </button>
                          <span
                            className={`flex-1 text-sm truncate ${
                              todo.completed ? 'text-muted-foreground line-through' : ''
                            }`}
                          >
                            {todo.text}
                          </span>
                          <button
                            onClick={() => removeTodo(todo.id)}
                            className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Activity - Full Width */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <div>
              <CardTitle>Recent activity</CardTitle>
              <CardDescription>
                Latest orders moving through your terminals.
              </CardDescription>
            </div>
            <Badge variant="outline" className="hidden text-xs md:inline-flex">
              Live feed
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {activityLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <img src="/blazepos-logo.png" alt="" className="h-4 w-4 animate-pulse" />
                  <span className="text-sm">Loading activity...</span>
                </div>
              </div>
            ) : recentOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                  <Receipt className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium">No recent orders</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Orders will appear here as they're placed
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {recentOrders.slice(0, 8).map((order) => {
                  const statusInfo = getStatusInfo(order.status);
                  const orderTypeIcon = order.status === 'sent' || order.status === 'in_progress' 
                    ? ChefHat 
                    : Clock;
                  const IconComponent = orderTypeIcon;
                  
                  return (
                    <div
                      key={order.id}
                      className="flex items-center gap-3 rounded-xl border bg-card/60 px-3 py-2"
                    >
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
                        <IconComponent className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          Order #{order.order_number}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {order.created_at ? getTimeAgo(order.created_at) : '—'}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-sm font-semibold">
                          {formatCurrency(order.total ?? 0)}
                        </span>
                        <Badge variant={statusInfo.variant}>
                          {statusInfo.label}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions - At Bottom for Admin Dashboard */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Quick actions</CardTitle>
          <CardDescription>
            {isCashier
              ? 'Essential tools for managing your register.'
              : 'Jump straight into the workflows you use most.'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Button
              variant="blaze"
              className="flex h-auto min-h-[6rem] flex-col items-start justify-between rounded-2xl px-4 py-3 text-left whitespace-normal"
              onClick={() => navigate('/sell/register')}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-black/10">
                <ShoppingCart className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <span className="block text-sm font-semibold">
                  New order
                </span>
                <span className="block text-xs text-white/80">
                  Start selling and create a new order.
                </span>
              </div>
            </Button>

            {isCashier ? (
              <>
                {/* Register session actions - dynamic based on session state */}
                {activeSession?.status === 'open' ? (
                  <>
                    {/* Take Break - pause session */}
                    <Button
                      variant="outline"
                      className="flex h-auto min-h-[6rem] flex-col items-start justify-between rounded-2xl px-4 py-3 text-left whitespace-normal"
                      onClick={() => pauseSession.mutate()}
                      disabled={pauseSession.isPending}
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10 text-blue-500">
                        <Coffee className="h-5 w-5" />
                      </div>
                      <div className="space-y-1">
                        <span className="block text-sm font-semibold">Take Break</span>
                        <span className="block text-xs text-muted-foreground">
                          Pause register temporarily.
                        </span>
                      </div>
                    </Button>
                    {/* End Shift - full settlement */}
                    <Button
                      variant="outline"
                      className="flex h-auto min-h-[6rem] flex-col items-start justify-between rounded-2xl px-4 py-3 text-left whitespace-normal"
                      onClick={() => navigate(`/close-register/${activeSession?.id}`)}
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/10 text-amber-500">
                        <LogOut className="h-5 w-5" />
                      </div>
                      <div className="space-y-1">
                        <span className="block text-sm font-semibold">End Shift</span>
                        <span className="block text-xs text-muted-foreground">
                          Close out and settle register.
                        </span>
                      </div>
                    </Button>
                  </>
                ) : activeSession?.status === 'paused' ? (
                  /* When paused, the PausedSessionOverlay will cover the screen
                     This button is here as a fallback but shouldn't normally be visible */
                  <Button
                    variant="outline"
                    className="flex h-auto min-h-[6rem] flex-col items-start justify-between rounded-2xl px-4 py-3 text-left whitespace-normal border-blue-500/50"
                    disabled
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10 text-blue-500">
                      <Coffee className="h-5 w-5" />
                    </div>
                    <div className="space-y-1">
                      <span className="block text-sm font-semibold">On Break</span>
                      <span className="block text-xs text-muted-foreground">
                        Enter PIN to resume.
                      </span>
                    </div>
                  </Button>
                ) : (
                  /* Open new register */
                  <Button
                    variant="outline"
                    className="flex h-auto min-h-[6rem] flex-col items-start justify-between rounded-2xl px-4 py-3 text-left whitespace-normal"
                    onClick={() => setShowOpenRegisterModal(true)}
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-500">
                      <DollarSign className="h-5 w-5" />
                    </div>
                    <div className="space-y-1">
                      <span className="block text-sm font-semibold">Open Register</span>
                      <span className="block text-xs text-muted-foreground">
                        Start your shift and open a till.
                      </span>
                    </div>
                  </Button>
                )}

                {/* Request Assistance */}
                <Button
                  variant="outline"
                  className="flex h-auto min-h-[6rem] flex-col items-start justify-between rounded-2xl px-4 py-3 text-left whitespace-normal relative"
                  onClick={() => setShowAssistanceModal(true)}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-500/10 text-red-500">
                    <Bell className="h-5 w-5" />
                  </div>
                  <div className="space-y-1">
                    <span className="block text-sm font-semibold">
                      {myActiveRequest ? 'Assistance Pending' : 'Request Assistance'}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {myActiveRequest ? 'Help is on the way.' : 'Call a supervisor for help.'}
                    </span>
                  </div>
                  {myActiveRequest && (
                    <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                  )}
                </Button>

                {/* Reprint Receipt */}
                <Button
                  variant="outline"
                  className="flex h-auto min-h-[6rem] flex-col items-start justify-between rounded-2xl px-4 py-3 text-left whitespace-normal"
                  onClick={() => setShowReceiptReprintDialog(true)}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10 text-blue-500">
                    <Receipt className="h-5 w-5" />
                  </div>
                  <div className="space-y-1">
                    <span className="block text-sm font-semibold">
                      Reprint Receipt
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      Select a transaction to reprint.
                    </span>
                  </div>
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  className="flex h-auto min-h-[6rem] flex-col items-start justify-between rounded-2xl px-4 py-3 text-left whitespace-normal"
                  onClick={() => navigate('/products')}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/5 text-primary">
                    <Flame className="h-5 w-5" />
                  </div>
                  <div className="space-y-1">
                    <span className="block text-sm font-semibold">{industryTerms.catalog}</span>
                    <span className="block text-xs text-muted-foreground">
                      Update items, pricing, and availability.
                    </span>
                  </div>
                </Button>

                <Button
                  variant="outline"
                  className="flex h-auto min-h-[6rem] flex-col items-start justify-between rounded-2xl px-4 py-3 text-left whitespace-normal"
                  onClick={() => navigate('/customers')}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-500">
                    <Users className="h-5 w-5" />
                  </div>
                  <div className="space-y-1">
                    <span className="block text-sm font-semibold">
                      Customers
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      View visit history and loyalty.
                    </span>
                  </div>
                </Button>

                <Button
                  variant="outline"
                  className="flex h-auto min-h-[6rem] flex-col items-start justify-between rounded-2xl px-4 py-3 text-left whitespace-normal"
                  onClick={() => navigate('/reports')}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-500/10 text-sky-500">
                    <TrendingUp className="h-5 w-5" />
                  </div>
                  <div className="space-y-1">
                    <span className="block text-sm font-semibold">
                      Reports
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      End-of-day and period summaries.
                    </span>
                  </div>
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      <OpenRegisterModal
        open={showOpenRegisterModal}
        onOpenChange={setShowOpenRegisterModal}
      />
      <RequestAssistanceModal
        open={showAssistanceModal}
        onOpenChange={setShowAssistanceModal}
      />
      <ReceiptReprintDialog
        open={showReceiptReprintDialog}
        onOpenChange={setShowReceiptReprintDialog}
      />
      <RetailMetricsModal
        open={showRetailMetricsModal}
        onOpenChange={setShowRetailMetricsModal}
        enabledWidgets={enabledWidgets}
        onSave={setEnabledWidgets}
      />
    </DashboardLayout>
  );
}
